import { queryOptions } from "@tanstack/react-query"
import { client } from "./client"
import { safeQueryOptions } from "@src/utils/query"
import {
  UnpackApiListPromise,
  UnpackPromise,
  UnpackArray,
} from "@src/utils/types"
import { components } from "./schema"
import { FormAnswerDto } from "./form"

export enum ApplicationStatus {
  /** Deprecated: nothing writes Apply any more. Kept in sync with the backend enum. */
  Apply = 0,
  Draft = 1,
  Pending = 2,
  Accepted = 3,
  Denied = 4,
}

export enum ApplicationOrderBy {
  DateNewest = 0,
  DateOldest = 1,
  HighestRating = 2,
  LowestRating = 3,
}

export enum EvaluationCompletionStatus {
  AllCompleted = 0,
  SomeCompleted = 1,
  NoneCompleted = 2,
}

export const applicationKeys = {
  all: ["applications"],
  userList: () => [...applicationKeys.all, "list", "user"],
}

export const applicationDefinitionKeys = {
  all: ["applicationDefinitions"],
  emailDefaults: () => [...applicationDefinitionKeys.all, "emailDefaults"],
  communityList: (id?: number) => [
    ...applicationDefinitionKeys.all,
    "list",
    "community",
    id,
  ],
  applicationDefinition: (id?: number) => [
    ...applicationKeys.all,
    "definition",
    id,
  ],
  // The public /ApplicantPreview endpoint returns the same DTO shape but is a
  // distinct resource (unauthenticated, applicant-facing). It must NOT share a
  // cache entry with the full authenticated definition — whichever query mounts
  // first would otherwise decide which endpoint backs the shared entry. Nested
  // under the definition key so response-level admin actions that invalidate
  // applicationDefinition(id) (Actions.tsx, TagsDialog, etc.) also refresh it.
  // Note: saving a definition invalidates the broader applicationDefinitionKeys.all
  // (['applicationDefinitions']) list, which prefix-matches neither this key nor
  // the full-definition key — a pre-existing gap, not introduced by this split.
  applicationDefinitionPreview: (id?: number) => [
    ...applicationDefinitionKeys.applicationDefinition(id),
    "preview",
  ],
  applicationDefinitionFilters: (id?: number) => [
    ...applicationKeys.all,
    "definition",
    id,
    "filters",
  ],
  // Filters sit last so this prefix matches every filter combination at once —
  // saving an evaluation can refresh the response list without knowing which
  // filters the list is currently using.
  applicationResponsesAllFilters: (id: number) => [
    ...applicationKeys.all,
    "definition",
    id,
    "responses",
  ],
  applicationResponses: (id: number, filters: ApplicationsFilters) => [
    ...applicationDefinitionKeys.applicationResponsesAllFilters(id),
    filters,
  ],
  applicationDefinitionsByGroup: (id?: number) => [
    ...applicationDefinitionKeys.all,
    "group",
    id,
  ],
}

export const applicationResponseKeys = {
  all: ["applicationResponses"],
  response: (id: number) => [...applicationResponseKeys.all, id],
  groupResponses: (groupId?: number) => [
    ...applicationResponseKeys.all,
    "group",
    groupId,
    "responses",
  ],
}

export type CommunityApplicationDetail = UnpackApiListPromise<
  ReturnType<typeof getCommunityApplicationDefinitions>
> & { evaluationRequired?: boolean; evaluationType?: string }

export const getCommunityApplicationDefinitions = async (id: number) => {
  const data = await client.GET("/api/Community/{id}/ApplicationDefinitions", {
    params: {
      path: { id },
    },
  })

  return data
}

export const getCommunityApplicationDefinitionsQuery = (id?: number) =>
  safeQueryOptions({
    queryFn: getCommunityApplicationDefinitions,
    queryKeys: applicationDefinitionKeys.communityList(id),
    unsafeQueryFnArgs: [id],
  })

export const getCommunityApplicationDefinition = async (id: number) => {
  const data = await client.GET("/api/ApplicationDefinition/{id}", {
    params: {
      path: { id },
    },
  })

  // evaluationRequired and evaluationType are computed at the component layer
  // using computeEvaluationFields() from ApplicationDefinitionPage/utils.ts
  return data as CommunityApplicationDetail
}

export type ApplicationDefinition = UnpackPromise<
  ReturnType<typeof getCommunityApplicationDefinition>
>

export const getCommunityApplicationDefinitionQuery = (id?: number) =>
  safeQueryOptions({
    queryFn: getCommunityApplicationDefinition,
    queryKeys: applicationDefinitionKeys.applicationDefinition(id),
    unsafeQueryFnArgs: [id],
  })

export const getCommunityApplicationDefinitionPreview = async (id: number) => {
  const data = await client.GET(
    "/api/ApplicationDefinition/{id}/ApplicantPreview",
    {
      params: {
        path: { id },
      },
    },
  )

  return data
}

export const getCommunityApplicationDefinitionPreviewQuery = (id?: number) =>
  safeQueryOptions({
    queryFn: getCommunityApplicationDefinitionPreview,
    queryKeys: applicationDefinitionKeys.applicationDefinitionPreview(id),
    unsafeQueryFnArgs: [id],
  })

export const deleteCommunityApplicationDefinition = async (id: number) => {
  const data = await client.DELETE("/api/ApplicationDefinition/{id}", {
    params: {
      path: { id },
    },
  })

  return data
}

export const saveCommunityApplicationDefinition = async (
  payload: CommunityApplicationDetail,
  id?: number,
) => {
  if (!id) {
    const data = await client.POST("/api/ApplicationDefinition", {
      body: payload,
    })

    return data
  }

  const data = await client.PUT("/api/ApplicationDefinition/{id}", {
    params: {
      path: { id },
    },
    body: payload,
  })

  return data
}

export const getCurrentUserApplications = async () => {
  const data = await client.GET("/api/Application/GetCurrentUserApplications")

  return data
}

export const getCurrentUserApplicationsQuery = () =>
  queryOptions({
    queryKey: ["currentUserApplications", "list"],
    queryFn: () => getCurrentUserApplications(),
  })

export type CurrentUserApplication = UnpackApiListPromise<
  ReturnType<typeof getCurrentUserApplications>
> & {
  // BAB-766: the backend list DTO ships these, but the checked-in swagger spec
  // (and therefore schema.ts) hasn't been regenerated yet. Remove this
  // intersection once the spec regen lands. Consumers fail open when absent.
  roundType?: number | null
  notifyApplicants?: boolean | null
}

export type ApplicationsFilters = components["schemas"]["ApplicationFilter"]
export type ApplicationRoundFilter =
  components["schemas"]["ApplicationRoundFilter"]

export const getApplicationDefinitionResponses = async (
  id: number,
  filters: ApplicationsFilters,
) => {
  const data = await client.POST(
    "/api/ApplicationDefinition/{id}/Applications",
    {
      params: {
        path: { id },
      },
      body: filters,
    },
  )

  return data
}

export type ApplicationsResponses = UnpackPromise<
  ReturnType<typeof getApplicationDefinitionResponses>
>

export type ApplicationResponse = UnpackArray<ApplicationsResponses["list"]>
export type ApplicationDetail = components["schemas"]["ApplicationDetailDto"]
export type ApplicationEvaluation = components["schemas"]["EvaluationDto"]
export type ApplicationScoreDetail =
  components["schemas"]["ApplicationScoreDetailDto"]

export const getApplicationDefinitionResponsesQuery = (
  applicationId: number | undefined,
  filters: ApplicationsFilters,
) =>
  safeQueryOptions({
    queryFn: getApplicationDefinitionResponses,
    queryKeys: applicationDefinitionKeys.applicationResponses(
      applicationId ?? 0,
      filters,
    ),
    unsafeQueryFnArgs: [applicationId, filters],
  })

export const getApplicationsFilters = async (id: number) => {
  const data = await client.GET(
    "/api/ApplicationDefinition/{id}/ApplicationsFilters",
    {
      params: {
        path: { id },
      },
    },
  )

  return data
}

export type ApplicationFilterValues = UnpackPromise<
  ReturnType<typeof getApplicationsFilters>
>

export const getApplicationsFiltersQuery = (id?: number) =>
  safeQueryOptions({
    queryFn: getApplicationsFilters,
    queryKeys: applicationDefinitionKeys.applicationDefinitionFilters(id),
    unsafeQueryFnArgs: [id],
  })

export const saveApplicationTags = async (
  applicationId: number,
  tags: string[],
) => {
  await client.POST("/api/Application/{id}/SaveTags", {
    params: {
      path: { id: applicationId },
    },
    body: {
      tags,
    },
  })
}

type GroupSaveTagsDto = components["schemas"]["GroupSaveTagsDto"]

export const saveGroupTags = async (
  applicationDefinitionId: number,
  tags: GroupSaveTagsDto["editApplicationTagsDto"],
  filters: GroupSaveTagsDto["applicationFilter"],
): Promise<void> => {
  const dto: GroupSaveTagsDto = {
    editApplicationTagsDto: tags,
    applicationFilter: filters,
  }

  await client.POST(
    "/api/Application/{applicationDefinitionId}/GroupSaveTags",
    {
      params: {
        path: { applicationDefinitionId },
      },
      body: dto,
    },
  )
}

export const acceptApplication = async (id: number, methodologyId?: number) => {
  await client.POST("/api/Application/{id}/AcceptApplication", {
    params: {
      path: { id },
      query: {
        methodologyId,
      },
    },
  })
}

export const acceptAllFilteredApplications = async (
  applicationDefinitionId: number,
  filters: ApplicationsFilters,
) => {
  await client.POST(
    "/api/Application/{applicationDefinitionId}/GroupAcceptApplication",
    {
      params: {
        path: { applicationDefinitionId },
      },
      body: filters,
    },
  )
}

export const rejectApplication = async (id: number) => {
  await client.POST("/api/Application/{id}/RejectApplication", {
    params: {
      path: { id },
    },
  })
}

export const rejectAllFilteredApplications = async (
  applicationDefinitionId: number,
  filters: ApplicationsFilters,
) => {
  await client.POST(
    "/api/Application/{applicationDefinitionId}/GroupRejectApplication",
    {
      params: {
        path: { applicationDefinitionId },
      },
      body: filters,
    },
  )
}

export const deleteApplication = async (id: number) => {
  await client.DELETE("/api/Application/{id}/DeleteApplication", {
    params: {
      path: { id },
    },
  })
}

export const deleteAllFilteredApplications = async (
  applicationDefinitionId: number,
  filter: ApplicationsFilters,
) => {
  await client.DELETE(
    "/api/Application/{applicationDefinitionId}/GroupDeleteApplication",
    {
      params: {
        path: { applicationDefinitionId },
      },
      body: filter,
    },
  )
}

export const revertApplicationToDrafted = async (
  communityId: number,
  ids: number[],
): Promise<ApplicationDetail[]> => {
  const payload = {
    communityId,
    ids,
    status: ApplicationStatus.Draft,
  }

  const data = await client.PUT(
    "/api/Application/UpdateApplicationStatusToDraft",
    {
      body: payload,
    },
  )

  return data
}

export const revertAllFilteredApplicationsToDrafted = async (
  applicationDefinitionId: number,
  filters: ApplicationsFilters,
): Promise<void> => {
  await client.POST(
    "/api/Application/{applicationDefinitionId}/GroupUpdateApplicationStatusToDraft",
    {
      params: {
        path: { applicationDefinitionId },
      },
      body: filters,
    },
  )
}

export const downloadApplicationDefinitionResponsesCsv = async (
  id: number,
  filters: ApplicationsFilters = {},
) => {
  const data = await client.POST(
    "/api/ApplicationDefinition/{id}/Applications-csv",
    {
      params: {
        path: { id },
      },
      body: filters,
    },
  )

  return data
}

export const downloadApplicationDefinitionEvaluationsCsv = async (
  id: number,
  filters: ApplicationsFilters = {},
) => {
  const data = await client.POST(
    "/api/ApplicationDefinition/{id}/ApplicationEvaluations-csv",
    {
      params: {
        path: { id },
      },
      body: filters,
    },
  )

  return data
}

export const downloadApplicationDefinitionRoundsCsv = async (
  applicationGroupId: number,
  filters: ApplicationRoundFilter = {},
) => {
  const data = await client.POST(
    "/api/ApplicationDefinition/{applicationGroupId}/ApplicationsRounds-csv",
    {
      params: {
        path: { applicationGroupId },
      },
      body: filters,
    },
  )

  return data
}

export const downloadApplicationDefinitionEvaluationsRoundsCsv = async (
  applicationGroupId: number,
  filters: ApplicationRoundFilter = {},
) => {
  const data = await client.POST(
    "/api/ApplicationDefinition/{applicationGroupId}/ApplicationEvaluationsRounds-csv",
    {
      params: {
        path: { applicationGroupId },
      },
      body: filters,
    },
  )

  return data
}

export const getApplicationResponse = async (id: number) => {
  const data = await client.GET("/api/Application/{id}", {
    params: {
      path: { id },
    },
  })

  return data
}

export type ApplicationResponseDetails = UnpackPromise<
  ReturnType<typeof getApplicationResponse>
>

export const getApplicationResponseQuery = (id: number) => {
  return queryOptions({
    queryKey: applicationResponseKeys.response(id),
    queryFn: () => getApplicationResponse(id),
  })
}

export type ApplicationScore = {
  criteria: string
  score: number
}

export const updateApplicationScores = async (
  applicationId: number,
  scores: ApplicationScore[],
) => {
  await client.PUT("/api/Application/{id}/Scores", {
    params: {
      path: { id: applicationId },
    },
    body: scores,
  })
}

type AttachmentsByQuestionRequestDto =
  components["schemas"]["AttachmentsByQuestionRequestDto"]

// use axios directly here to handle blob response
export const downloadAttachmentsByQuestion = async (
  payload: AttachmentsByQuestionRequestDto,
) => {
  const response = await client.axiosInstance.post(
    "/api/Application/DownloadAttachmentsByQuestion",
    payload,
    { responseType: "blob" },
  )

  return response.data
}
type GroupAttachmentsByQuestionRequestDto =
  components["schemas"]["GroupAttachmentsByQuestionRequestDto"]

export const downloadGroupAttachmentsByQuestion = async (
  applicationDefinitionId: number,
  payload: GroupAttachmentsByQuestionRequestDto,
) => {
  const response = await client.axiosInstance.post(
    `/api/Application/${applicationDefinitionId}/GroupDownloadAttachmentsByQuestion`,
    payload,
    { responseType: "blob" },
  )

  return response.data
}

// Rule ids are the server's dedup key for the reminder log: an edited rule
// must go back up with the id the server issued, and only a rule the admin
// just added goes up as 0 — re-creating an existing offset under a new id
// re-emails everyone still in draft.
export type ApplicationReminderRuleDto =
  components["schemas"]["ApplicationReminderRuleDto"]

// use axios directly here to handle blob response
export const downloadApplicationPdf = async (applicationId: number) => {
  const response = await client.axiosInstance.get(
    `/api/Application/${applicationId}/Pdf`,
    { responseType: "blob" },
  )

  return response.data
}

type GroupExportPdfRequestDto =
  components["schemas"]["GroupExportPdfRequestDto"]

export const downloadApplicationsPdfZip = async (
  applicationDefinitionId: number,
  payload: GroupExportPdfRequestDto,
) => {
  const response = await client.axiosInstance.post(
    `/api/Application/${applicationDefinitionId}/GroupExportPdf`,
    payload,
    { responseType: "blob" },
  )

  return response.data
}

/**
 * The stock round email bodies, in the current user's language and written
 * with the {{FirstName}}-style placeholders — the same text the backend sends
 * while an email has no custom body (BAB-1048).
 */
export const getApplicationEmailDefaults = async () => {
  const data = await client.GET("/api/ApplicationDefinition/EmailDefaults")

  return data
}

export const getApplicationEmailDefaultsQuery = () =>
  queryOptions({
    queryFn: getApplicationEmailDefaults,
    queryKey: applicationDefinitionKeys.emailDefaults(),
    staleTime: Infinity,
  })

export const getApplicationDefinitionsByGroupId = async (id: number) => {
  const data = await client.GET("/api/ApplicationDefinition/GroupById/{id}", {
    params: {
      path: { id: String(id) },
      query: {
        applicationDefinitionGroupId: id,
      },
    },
  })

  return data
}

export const getApplicationDefinitionsByGroupIdQuery = (id?: number) =>
  safeQueryOptions({
    queryFn: getApplicationDefinitionsByGroupId,
    queryKeys: applicationDefinitionKeys.applicationDefinitionsByGroup(id),
    unsafeQueryFnArgs: [id],
  })

export const createApplicationDefinitionRounds = async (
  applications: CommunityApplicationDetail[],
) => {
  console.log(applications)
  const data = await client.POST(
    "/api/ApplicationDefinition/CreateApplicationDefinitionRounds",
    {
      body: applications,
    },
  )

  return data
}

export const addApplicationDefinitionRound = async (
  applicationDefinition: CommunityApplicationDetail,
) => {
  const data = await client.POST(
    "/api/ApplicationDefinition/AddApplicationDefinitionRound",
    {
      body: applicationDefinition,
    },
  )

  return data
}

export const saveApplication = async (
  payload: ApplicationDetail,
  applicationId?: number,
) => {
  if (!applicationId) {
    const data = await client.POST("/api/Application", {
      body: payload,
    })

    return data
  }

  const data = await client.PUT("/api/Application/{id}", {
    params: {
      path: { id: applicationId },
    },
    body: payload,
  })

  return data
}

export type ApplicationSubmission = UnpackPromise<
  ReturnType<typeof saveApplication>
>

export const saveApplicationAnswers = async (
  applicationId: number,
  answers: FormAnswerDto[],
) => {
  const data = await client.PUT("/api/Application/{id}/Answers", {
    params: {
      path: { id: applicationId },
    },
    body: answers,
  })

  return data
}

export const updateApplicationStatus = async (
  applicationId: number,
  status: number,
) => {
  const data = await client.PUT(
    "/api/Application/UpdateApplicationStatus/{id}",
    {
      params: {
        path: { id: applicationId },
        query: { status: status as ApplicationStatus },
      },
    },
  )

  return data
}

export enum ApplicationDefinitionRoundType {
  Form = 0,
  InterviewAndForm = 1,
  InterviewOnly = 2,
  EvaluationOnly = 3,
}

export const getApplicationRoundResponsesByGroupId = async (
  groupId: number,
) => {
  const data = await client.GET(
    "/api/Application/group/{applicationResponseGroupId}",
    {
      params: {
        path: { applicationResponseGroupId: groupId },
      },
    },
  )

  return data
}

export const getApplicationRoundResponsesByGroupIdQuery = (groupId?: number) =>
  safeQueryOptions({
    queryFn: getApplicationRoundResponsesByGroupId,
    queryKeys: applicationResponseKeys.groupResponses(groupId),
    unsafeQueryFnArgs: [groupId],
  })
export const getApplicationEvaluation = async (applicationId: number) => {
  const data = await client.GET("/api/Application/{applicationId}/Evaluation", {
    params: {
      path: { applicationId },
    },
  })

  return data
}

export const getApplicationEvaluationQuery = (applicationId: number) => {
  return queryOptions({
    queryKey: [
      ...applicationResponseKeys.response(applicationId),
      "evaluation",
    ],
    queryFn: () => getApplicationEvaluation(applicationId),
  })
}

export enum EvaluationAnswerStatus {
  Draft = 0,
  Submitted = 1,
}

export const saveApplicationEvaluationDraft = async (
  applicationId: number,
  evaluation: FormAnswerDto[],
) =>
  client.POST("/api/Application/{applicationId}/SaveEvaluationDraft", {
    params: {
      path: { applicationId },
    },
    body: evaluation,
  })

export const submitApplicationEvaluation = async (
  applicationId: number,
  evaluation: FormAnswerDto[],
) => {
  if (evaluation[0].id) {
    return client.PUT("/api/Application/{applicationId}/UpdateEvaluation", {
      params: {
        path: { applicationId },
      },
      body: evaluation,
    })
  } else {
    return client.POST("/api/Application/{applicationId}/SubmitEvaluation", {
      params: {
        path: { applicationId },
      },
      body: evaluation,
    })
  }
}

export type AllEvaluationsDto = components["schemas"]["AllEvaluationsDto"]
export type EvaluatorEvaluationsDto =
  components["schemas"]["EvaluatorEvaluationsDto"]

export const getApplicationAllEvaluations = async (applicationId: number) => {
  const data = await client.GET(
    "/api/Application/{applicationId}/Evaluations",
    {
      params: {
        path: { applicationId },
      },
    },
  )

  return data
}

export const getApplicationAllEvaluationsQuery = (applicationId: number) => {
  return queryOptions({
    queryKey: [
      ...applicationResponseKeys.response(applicationId),
      "allEvaluations",
    ],
    queryFn: () => getApplicationAllEvaluations(applicationId),
  })
}
